import { useQuery, useQueryClient } from '@tanstack/react-query';
import { campaignsApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { CACHE_CONFIG } from '@/config/constants';
import { features } from '@/config/features';

export function useCampaignsQuery() {
  const { isAuthenticated } = useAuthStore();
  const freshness = features.campaignDataFreshness;

  // Determine if we should use cached data based on freshness setting
  // For refresh_on_login: We DON'T use cached (use_cached=false) because we want
  // performance metrics from campaigns_data_hourly, which are prefetched on login
  const useCached = false; // Always fetch fresh data to get performance metrics

  // Adjust stale time based on freshness mode
  const staleTime = freshness === 'refresh_always'
    ? 0 // Always fetch fresh
    : freshness === 'refresh_on_first_nav'
    ? CACHE_CONFIG.DEFAULT_DURATION // Cache for session (5 minutes)
    : Infinity; // Cache forever (until logout) - refresh_on_login mode

  return useQuery({
    queryKey: ['campaigns', useCached],
    queryFn: () => campaignsApi.getCampaigns(useCached),
    enabled: isAuthenticated,
    staleTime: staleTime,
    gcTime: CACHE_CONFIG.DEFAULT_DURATION * 2,
    retry: 2,
    refetchOnMount: freshness === 'refresh_always' ? 'always' : false, // Only refetch on mount in refresh_always mode
  });
}

/**
 * Hook to prefetch campaigns data on login.
 * This ensures fresh performance metrics are available for refresh_on_login mode.
 */
export function usePrefetchCampaignsOnLogin() {
  const queryClient = useQueryClient();
  const freshness = features.campaignDataFreshness;

  return async () => {
    // Only prefetch for refresh_on_login mode
    if (freshness !== 'refresh_on_login') {
      return;
    }

    // Prefetch campaigns with fresh data (use_cached=false)
    await queryClient.prefetchQuery({
      queryKey: ['campaigns', false],
      queryFn: () => campaignsApi.getCampaigns(false),
      staleTime: Infinity, // Cache for the entire session
    });
  };
}

export function useCampaignQuery(campaignId: string) {
  const { isAuthenticated } = useAuthStore();
  const freshness = features.campaignDataFreshness;

  // For refresh_on_login mode:
  // - We fetch fresh data (use_cached=false) to get performance metrics
  // - But cache it with Infinity staleTime so it persists for the session
  const useCached = false; // Always fetch fresh to get performance metrics

  // Adjust stale time based on freshness mode
  const staleTime = freshness === 'refresh_always'
    ? 0 // Always fetch fresh
    : freshness === 'refresh_on_first_nav'
    ? CACHE_CONFIG.DEFAULT_DURATION // Cache for session (5 minutes)
    : Infinity; // Cache forever (until logout) - refresh_on_login mode

  return useQuery({
    queryKey: ['campaign', campaignId, useCached],
    queryFn: () => campaignsApi.getCampaign(campaignId, useCached),
    enabled: isAuthenticated && !!campaignId,
    staleTime: staleTime,
    gcTime: CACHE_CONFIG.DEFAULT_DURATION * 2,
    retry: 2,
    // For refresh_on_login: use cached data (don't refetch on mount)
    // For other modes: allow default refetch behavior
    refetchOnMount: freshness === 'refresh_on_login' ? false : undefined,
  });
}